/**
 * Moving a leg between trades, from the drawer — ROADMAP 9.9.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO OPTIMISTIC UPDATE, ON PURPOSE
 *
 * Everything else that changes this drawer is optimistic or near enough. This
 * is not, because the server is allowed to refuse on a read the browser never
 * saw — a stop that fired, an exit dispatched from another tab — and showing
 * the leg in its new basket before that read has happened would be showing a
 * position in a place it may never go.
 *
 * So the row stays put until the move has committed, and the book is re-read
 * from the one authority. `projectTrade` publishes the book-updated
 * notification on the way out, so in practice the re-read is already in
 * flight before the response lands.
 *
 * ONE MOVE AT A TIME, like the exit path and for the same reason: a second
 * drop decided against a book that is mid-change is deciding against numbers
 * that no longer describe anything.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useState } from 'react'
import { api } from '@/lib/api'

/** A move that committed, and what the drawer should do about it. */
export interface LandedMove {
  readonly tradeLegId: string
  readonly symbol: string
  /** Where it went — a strategy's name, or "Manual". */
  readonly into: string
  /**
   * Whether to ASK for a role. True only when the leg joined a basket:
   * "Moved to Manual — what is this leg for?" is a question about nothing.
   * A detach gets the quiet one-line confirmation instead.
   */
  readonly ask: boolean
}

export interface LegTransferState {
  /** The leg whose move is in flight, or null. */
  readonly moving: string | null
  /** Why the last move was refused. Cleared on the next attempt. */
  readonly refusal: string | null
  /**
   * A move that LANDED, in words, until it is dismissed.
   *
   * Not decoration. A drop that misses — released in the gap between two
   * cards — looks exactly like a drop that worked: the row does not move
   * either way until the book is re-read, so silence means both "nothing
   * happened" and "it worked" at once. That ambiguity is what made the
   * feature read as broken while the database was correct, and the trader
   * dragged the same leg three more times.
   */
  readonly landed: LandedMove | null
}

export function useLegTransfer(onMoved: () => void): LegTransferState & {
  /** Into a basket, or out to manual with a null target; `into` names it. */
  move: (
    tradeLegId: string,
    targetTradeId: string | null,
    into: string,
    symbol: string,
  ) => Promise<void>
  dismiss: () => void
} {
  const [moving, setMoving] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [landed, setLanded] = useState<LandedMove | null>(null)

  const dismiss = useCallback(() => {
    setRefusal(null)
    setLanded(null)
  }, [])

  const move = useCallback(
    async (
      tradeLegId: string,
      targetTradeId: string | null,
      into: string,
      symbol: string,
    ): Promise<void> => {
      // A second drop while one is in flight is dropped, not queued.
      if (moving !== null) return
      setMoving(tradeLegId)
      setRefusal(null)
      setLanded(null)
      try {
        await api.post(`/trades/legs/${tradeLegId}/move`, { targetTradeId })
        setLanded({ tradeLegId, symbol, into, ask: targetTradeId !== null })
        onMoved()
      } catch (e: unknown) {
        /*
         * The server's own sentence, verbatim. Every refusal here names a
         * state the trader can see and act on — that basket is in another
         * account, it holds the opposite contract, this leg is being closed
         * — and rewording them in the browser would mean maintaining the
         * explanation in two places.
         */
        setRefusal(e instanceof Error ? e.message : 'The leg could not be moved.')
      } finally {
        setMoving(null)
      }
    },
    [moving, onMoved],
  )

  return { moving, refusal, landed, move, dismiss }
}
